import React from 'react';
import { useForm } from 'react-hook-form';
import { format, subMonths } from 'date-fns';
import Button from '../UI/Button';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAlertTriangle } = FiIcons;

const ProcessPayrollForm = ({ user, salary, onSubmit, onCancel, paidMonths = [] }) => {
  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    defaultValues: {
      paymentForMonth: format(subMonths(new Date(), 1), 'yyyy-MM'), // Default to previous month
      paymentDate: format(new Date(), 'yyyy-MM-dd'), // Default to today
    }
  });

  const selectedMonth = watch('paymentForMonth');
  const isDuplicate = paidMonths.includes(selectedMonth);

  // Calculate totals based on salary config
  const baseSalary = parseFloat(salary?.baseSalary || 0);
  const allowances = (parseFloat(salary?.housingAllowance || 0) + 
                     parseFloat(salary?.transportAllowance || 0) + 
                     parseFloat(salary?.otherAllowances || 0));
  const deductions = parseFloat(salary?.deductions || 0);
  const netSalary = baseSalary + allowances - deductions;
  
  const currency = salary?.currency || 'USD';
  const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : currency === 'INR' ? '₹' : currency === 'AED' ? 'AED ' : '$';

  const onFormSubmit = (data) => {
    onSubmit({
      ...data,
      baseSalary,
      allowances,
      deductions,
      netSalary
    });
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
        <h3 className="text-sm font-bold text-blue-900 uppercase tracking-wide mb-2">Employee Details</h3>
        <div className="flex justify-between items-center">
          <div>
            <p className="font-medium text-gray-900">{user.fullName || user.full_name}</p>
            <p className="text-xs text-gray-500">{user.role} • {user.email}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500">Configured Net Salary</p>
            <p className="text-xl font-bold text-green-700">{symbol}{netSalary.toLocaleString()}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Payment For Month *
          </label>
          <input
            type="month"
            {...register('paymentForMonth', { required: 'Please select the salary month' })}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-primary-500 focus:border-primary-500 ${isDuplicate ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'}`}
          />
          {isDuplicate ? (
            <div className="mt-1 text-sm text-yellow-700 font-medium flex items-center">
              <SafeIcon icon={FiAlertTriangle} className="h-4 w-4 mr-1" />
              Selected month is already paid for this employee.
            </div>
          ) : (
            <p className="text-xs text-gray-500 mt-1">The month this salary covers.</p>
          )}
          {errors.paymentForMonth && (
            <p className="mt-1 text-sm text-red-600">{errors.paymentForMonth.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Payment Date *
          </label>
          <input
            type="date"
            {...register('paymentDate', { required: 'Please select the payment date' })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
          />
          <p className="text-xs text-gray-500 mt-1">The actual date of payment/transfer.</p>
          {errors.paymentDate && (
            <p className="mt-1 text-sm text-red-600">{errors.paymentDate.message}</p>
          )}
        </div>
      </div>

      <div className="border-t border-gray-200 pt-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Payment Summary</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between text-gray-600">
            <span>Base Salary</span>
            <span>{symbol}{baseSalary.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-gray-600">
            <span>Total Allowances</span>
            <span>{symbol}{allowances.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-red-600">
            <span>Deductions</span>
            <span>-{symbol}{deductions.toLocaleString()}</span>
          </div>
          <div className="flex justify-between font-bold text-gray-900 pt-2 border-t border-gray-100">
            <span>Net Payable</span>
            <span>{symbol}{netSalary.toLocaleString()}</span>
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-2">
        <Button variant="outline" onClick={onCancel} type="button">
          Cancel
        </Button>
        <Button type="submit" variant={isDuplicate ? "danger" : "primary"}>
          {isDuplicate ? 'Confirm Duplicate Payment' : 'Confirm & Mark Paid'}
        </Button>
      </div>
    </form>
  );
};

export default ProcessPayrollForm;